import { Component, OnInit } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { AuthenticationService } from "@app/_services";
import { ApiBackendService } from "@app/_services/api-backend.service";
import { NzMessageService } from "ng-zorro-antd/message";
import { environment } from "../../../environments/environment";
import { saveAs } from "file-saver";

@Component({
  selector: "app-budget",
  templateUrl: "./budget.component.html",
  styleUrls: ["./budget.component.css"],
})
export class BudgetComponent implements OnInit {
  budgets: any[] = [];
  budgetGroups: { name: string; realistic: any; pessimistic: any; period: string; dateCreated: any; dateUpdated: any }[] = [];
  selectedComparisonGroup: any = null;
  loading = false;
  currentUser: any;
  userRole: string = '';
  readOnly = false;
  adminViewBudgetId: number | null = null;

  // View state
  currentView: 'landing' | 'create' | 'edit-list' | 'editor' | 'comparison' = 'landing';
  createMethod: 'none' | 'zero' | 'historic' = 'none';

  // Create budget form
  createForm = {
    name: "",
    sourceProcessId: null,
    periodStartMonth: 1,
    periodStartYear: new Date().getFullYear(),
    periodEndMonth: 12,
    periodEndYear: new Date().getFullYear(),
  };
  userFiles: any[] = [];
  loadingFiles = false;

  // Budget editor
  selectedBudget: any = null;
  selectedVariant: string = "realistic";
  budgetLines: any[] = [];
  savingBudget = false;

  // Cached computed values (avoid method calls in template)
  cachedUniqueRows: any[] = [];
  cachedMonthsInPeriod: { month: number; year: number; label: string }[] = [];
  lineValueMap: Map<string, any> = new Map();
  rowTotalMap: Map<number, number> = new Map();

  // Year expand/collapse for column grouping
  expandedYears: Set<number> = new Set();

  // Expandable P&L detail accounts (editable account lines)
  accountLinesBySort: { [sortOrder: string]: any[] } = {};
  expandSet: Set<number> = new Set();
  loadingAccountLines = false;
  budgetGuidanceContent: string = '';
  dirtyAccountLines: { id: number; value: number }[] = [];

  // Add account form
  addAccountSortOrder: number | null = null;
  addAccountCode = '';
  addAccountDesc = '';
  addingAccount = false;
  availableAccounts: { accountCode: string; accountDescription: string }[] = [];
  loadingAvailableAccounts = false;

  // Number formatting helpers for text inputs (Romanian locale: 1.234)
  formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  }

  getAddButtonLabel(sortOrder: number): string {
    if (sortOrder === 0 || sortOrder === 9 || sortOrder === 13) return '+ Adaugă venit';
    return '+ Adaugă cheltuială';
  }
  // Year grouping: computed properties and helpers
  get isAnnualMode(): boolean {
    return this.cachedMonthsInPeriod.length > 0 && this.cachedMonthsInPeriod[0].month === 0;
  }

  get yearGroups(): { year: number; monthCount: number }[] {
    if (this.isAnnualMode) return [];
    const groups: { year: number; monthCount: number }[] = [];
    const seen = new Set<number>();
    for (const mp of this.cachedMonthsInPeriod) {
      if (!seen.has(mp.year)) {
        seen.add(mp.year);
        groups.push({ year: mp.year, monthCount: this.cachedMonthsInPeriod.filter(m => m.year === mp.year).length });
      }
    }
    return groups;
  }

  get hasMultipleYears(): boolean {
    return this.yearGroups.length > 1;
  }

  get visibleColumns(): { month: number; year: number; label: string; isYearTotal?: boolean }[] {
    if (!this.hasMultipleYears) {
      return this.cachedMonthsInPeriod;
    }
    const result: { month: number; year: number; label: string; isYearTotal?: boolean }[] = [];
    for (const yg of this.yearGroups) {
      if (this.expandedYears.has(yg.year)) {
        result.push(...this.cachedMonthsInPeriod.filter(mp => mp.year === yg.year));
      } else {
        result.push({ month: 0, year: yg.year, label: `Total ${yg.year}`, isYearTotal: true });
      }
    }
    return result;
  }

  toggleYear(year: number): void {
    if (this.expandedYears.has(year)) {
      this.expandedYears.delete(year);
    } else {
      this.expandedYears.add(year);
    }
  }

  getYearColspan(year: number): number {
    if (this.expandedYears.has(year)) {
      return this.cachedMonthsInPeriod.filter(mp => mp.year === year).length;
    }
    return 1;
  }

  getYearTotal(sortOrder: number, year: number): number {
    return this.cachedMonthsInPeriod
      .filter(mp => mp.year === year)
      .reduce((sum, mp) => sum + (this.getLineValue(sortOrder, mp.month, mp.year)?.value || 0), 0);
  }

  getAccountYearTotal(acct: any, year: number): number {
    return this.cachedMonthsInPeriod
      .filter(mp => mp.year === year)
      .reduce((sum, mp) => sum + (this.getAccountValue(acct, mp.month, mp.year) || 0), 0);
  }

  parseNumber(text: string): number {
    if (!text) return 0;
    // Romanian: . = thousand sep, , = decimal sep
    return parseFloat(text.replace(/\./g, '').replace(',', '.')) || 0;
  }

  // Months
  months = [
    { value: 1, label: "Ian" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "Mai" },
    { value: 6, label: "Iun" },
    { value: 7, label: "Iul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];

  monthsFull = [
    { value: 1, label: "Ianuarie" },
    { value: 2, label: "Februarie" },
    { value: 3, label: "Martie" },
    { value: 4, label: "Aprilie" },
    { value: 5, label: "Mai" },
    { value: 6, label: "Iunie" },
    { value: 7, label: "Iulie" },
    { value: 8, label: "August" },
    { value: 9, label: "Septembrie" },
    { value: 10, label: "Octombrie" },
    { value: 11, label: "Noiembrie" },
    { value: 12, label: "Decembrie" },
  ];

  constructor(
    private apiBackend: ApiBackendService,
    private authService: AuthenticationService,
    private httpClient: HttpClient,
    private message: NzMessageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.currentUserValue;
    this.userRole = this.authService.currentUserInfo?.role || '';
    if (this.userRole !== 'admin') {
      this.loadBudgets();
      this.loadBudgetGuidance();
    }
  }

  loadBudgetGuidance(): void {
    this.apiBackend.getTextSlideByKey('budget_guidance').subscribe(
      (res: any) => {
        if (res.body?.content) {
          this.budgetGuidanceContent = res.body.content;
        }
      },
      () => {} // Silently ignore if not found
    );
  }

  loadBudgets() {
    this.loading = true;
    this.apiBackend.getBudgets().subscribe(
      (res) => {
        this.budgets = res.body;
        this._groupBudgets();
        this.loading = false;
      },
      (err) => {
        this.loading = false;
        this.message.error("Eroare la încărcarea bugetelor");
      }
    );
  }

  private _groupBudgets() {
    const grouped = new Map<string, { realistic: any; pessimistic: any }>();
    for (const b of this.budgets) {
      if (!grouped.has(b.name)) grouped.set(b.name, { realistic: null, pessimistic: null });
      const g = grouped.get(b.name)!;
      if (b.variantType === 'realistic') g.realistic = b;
      else g.pessimistic = b;
    }
    this.budgetGroups = Array.from(grouped.entries()).map(([name, g]) => {
      const ref = g.realistic || g.pessimistic;
      const rDate = g.realistic?.dateUpdated ? new Date(g.realistic.dateUpdated) : null;
      const pDate = g.pessimistic?.dateUpdated ? new Date(g.pessimistic.dateUpdated) : null;
      const dateUpdated = rDate && pDate ? (rDate > pDate ? g.realistic.dateUpdated : g.pessimistic.dateUpdated)
        : (rDate ? g.realistic.dateUpdated : pDate ? g.pessimistic.dateUpdated : ref.dateCreated);
      return {
        name,
        realistic: g.realistic,
        pessimistic: g.pessimistic,
        period: `${this.months[ref.periodStartMonth - 1]?.label} ${ref.periodStartYear} - ${this.months[ref.periodEndMonth - 1]?.label} ${ref.periodEndYear}`,
        dateCreated: ref.dateCreated,
        dateUpdated,
      };
    });
  }

  // Navigation methods
  goToCreate() {
    this.currentView = 'create';
    this.createMethod = 'none';
    this.loadUserFiles();
  }

  selectCreateMethod(method: 'zero' | 'historic') {
    this.createMethod = method;
    if (method === 'zero') {
      this.createForm.sourceProcessId = null;
    }
  }

  goToEditList() {
    this.currentView = 'edit-list';
    this.loadBudgets();
  }

  goToEditRealist() {
    if (this.budgetGroups.length > 0 && this.budgetGroups[0].realistic) {
      this.openBudget(this.budgetGroups[0].realistic);
    }
  }

  goToEditPesimist() {
    if (this.budgetGroups.length > 0 && this.budgetGroups[0].pessimistic) {
      this.openBudget(this.budgetGroups[0].pessimistic);
    }
  }

  switchVariant() {
    if (!this.selectedBudget || this.budgetGroups.length === 0) return;
    const group = this.budgetGroups[0];
    const other = this.selectedBudget.variantType === 'realistic' ? group.pessimistic : group.realistic;
    if (other) this.openBudget(other);
  }

  goToComparison() {
    this.currentView = 'comparison';
    this.selectedComparisonGroup = this.budgetGroups.length > 0 ? this.budgetGroups[0] : null;
    this.loadUserFiles();
  }

  backToLanding() {
    this.currentView = 'landing';
    this.createMethod = 'none';
    this.selectedBudget = null;
    this.selectedComparisonGroup = null;
    this.budgetLines = [];
    this.cachedUniqueRows = [];
    this.cachedMonthsInPeriod = [];
    this.lineValueMap.clear();
    this.rowTotalMap.clear();
    this.accountLinesBySort = {};
    this.expandSet.clear();
    this.dirtyAccountLines = [];
    this.loadBudgets();
  }

  selectComparisonGroup(group: any) {
    this.selectedComparisonGroup = group;
  }

  loadUserFiles() {
    this.loadingFiles = true;
    this.apiBackend.getBudgetSources().subscribe(
      (res) => {
        this.userFiles = res.body || [];
        this.loadingFiles = false;
      },
      (err) => {
        this.loadingFiles = false;
        this.message.error("Eroare la încărcarea rapoartelor");
      }
    );
  }

  getFileLabel(f: any): string {
    if (f.month && f.year) {
      const monthLabel = this.months[f.month - 1]?.label || f.month;
      return `${monthLabel} ${f.year}`;
    }
    return f.fileName || 'Raport';
  }

  createBudget() {
    if (!this.createForm.name) {
      this.message.warning("Completează numele bugetului");
      return;
    }

    this.loading = true;

    // Create both variants
    const baseData: any = {
      name: this.createForm.name,
      periodStartMonth: this.createForm.periodStartMonth,
      periodStartYear: this.createForm.periodStartYear,
      periodEndMonth: this.createForm.periodEndMonth,
      periodEndYear: this.createForm.periodEndYear,
    };
    if (this.createForm.sourceProcessId) {
      baseData.sourceProcessId = this.createForm.sourceProcessId;
    }

    // Create realistic variant
    this.apiBackend
      .createBudget({ ...baseData, variantType: "realistic" })
      .subscribe(
        (res) => {
          // Create pessimistic variant
          this.apiBackend
            .createBudget({ ...baseData, variantType: "pessimistic" })
            .subscribe(
              (res2) => {
                this.message.success("Bugetele au fost create cu succes");
                this.loading = false;
                this.currentView = 'landing';
                this.createMethod = 'none';
                this.loadBudgets();
              },
              (err) => {
                this.loading = false;
                this.message.error(
                  "Eroare la crearea variantei pesimiste"
                );
              }
            );
        },
        (err) => {
          this.loading = false;
          this.message.error("Eroare la crearea bugetului");
        }
      );
  }

  openBudget(budget: any) {
    this.currentView = 'editor';
    this.selectedBudget = budget;
    this.loadBudgetDetail(budget.id);
  }

  loadBudgetDetail(budgetId: number) {
    this.loading = true;
    this.apiBackend.getBudget(budgetId).subscribe(
      (res) => {
        this.selectedBudget = res.body;
        this.budgetLines = res.body.lines || [];
        this.rebuildCaches();
        this.loading = false;
        this.loadAccountLines(budgetId);
      },
      (err) => {
        this.loading = false;
        this.message.error("Eroare la încărcarea bugetului");
      }
    );
  }

  loadAccountLines(budgetId: number) {
    this.loadingAccountLines = true;
    this.dirtyAccountLines = [];
    this.apiBackend.getBudgetAccountLines(budgetId).subscribe(
      (res) => {
        this.accountLinesBySort = res.body?.accounts || {};
        this.loadingAccountLines = false;
      },
      () => {
        this.loadingAccountLines = false;
      }
    );
  }

  backToList() {
    this.currentView = 'landing';
    this.loadBudgets();
    this.selectedBudget = null;
    this.budgetLines = [];
    this.cachedUniqueRows = [];
    this.cachedMonthsInPeriod = [];
    this.lineValueMap.clear();
    this.rowTotalMap.clear();
    this.accountLinesBySort = {};
    this.expandSet.clear();
    this.dirtyAccountLines = [];
  }

  onExpandChange(sortOrder: number, checked: boolean) {
    if (checked) {
      this.expandSet.add(sortOrder);
    } else {
      this.expandSet.delete(sortOrder);
    }
  }

  hasDetailAccounts(sortOrder: number): boolean {
    const accts = this.accountLinesBySort[String(sortOrder)];
    return accts && accts.length > 0;
  }

  getDetailAccounts(sortOrder: number): any[] {
    return this.accountLinesBySort[String(sortOrder)] || [];
  }

  isEditableAccountLine(acct: any): boolean {
    return acct.values !== undefined && acct.lineIds !== undefined;
  }

  getAccountValue(acct: any, month: number, year: number): number {
    if (acct.values) {
      return acct.values[`${month}-${year}`] || 0;
    }
    return 0;
  }

  getAccountTotal(acct: any): number {
    if (!acct.values) return acct.value || 0;
    let total = 0;
    for (const mp of this.cachedMonthsInPeriod) {
      total += (acct.values[`${mp.month}-${mp.year}`] || 0);
    }
    return total;
  }

  onAccountValueChange(sortOrder: number, accountCode: string, month: number, year: number, newValue: number) {
    const accts = this.accountLinesBySort[String(sortOrder)];
    if (!accts) return;
    const acct = accts.find((a: any) => a.accountCode === accountCode);
    if (!acct || !acct.values || !acct.lineIds) return;

    const mk = `${month}-${year}`;
    acct.values[mk] = newValue;

    // Track dirty line
    const lineId = acct.lineIds[mk];
    if (lineId) {
      const existing = this.dirtyAccountLines.find((d) => d.id === lineId);
      if (existing) {
        existing.value = newValue;
      } else {
        this.dirtyAccountLines.push({ id: lineId, value: newValue });
      }
    }

    // Recalculate parent row value for this month
    let parentTotal = 0;
    for (const a of accts) {
      parentTotal += (a.values?.[mk] || 0);
    }
    const parentLine = this.lineValueMap.get(`${sortOrder}-${month}-${year}`);
    if (parentLine) {
      parentLine.value = parentTotal;
      // Recalculate row total
      let rowTotal = 0;
      for (const mp of this.cachedMonthsInPeriod) {
        const l = this.lineValueMap.get(`${sortOrder}-${mp.month}-${mp.year}`);
        if (l) rowTotal += (l.value || 0);
      }
      this.rowTotalMap.set(sortOrder, rowTotal);
    }
  }

  showAddAccountForm(sortOrder: number) {
    this.addAccountSortOrder = sortOrder;
    this.addAccountCode = '';
    this.addAccountDesc = '';
    this.availableAccounts = [];
    if (this.selectedBudget) {
      this.loadingAvailableAccounts = true;
      this.apiBackend.getBudgetAvailableAccounts(this.selectedBudget.id, sortOrder).subscribe(
        (res) => {
          this.availableAccounts = res.body?.accounts || [];
          this.loadingAvailableAccounts = false;
        },
        () => { this.loadingAvailableAccounts = false; }
      );
    }
  }

  onAddAccountSelect(accountCode: string) {
    this.addAccountCode = accountCode;
    const found = this.availableAccounts.find(a => a.accountCode === accountCode);
    this.addAccountDesc = found ? found.accountDescription : '';
  }

  cancelAddAccount() {
    this.addAccountSortOrder = null;
  }

  confirmAddAccount() {
    if (!this.selectedBudget || this.addAccountSortOrder === null || !this.addAccountCode) {
      this.message.warning('Completează toate câmpurile');
      return;
    }
    this.addingAccount = true;
    this.apiBackend.addBudgetAccountLine(
      this.selectedBudget.id,
      this.addAccountSortOrder,
      this.addAccountCode,
      this.addAccountDesc
    ).subscribe(
      () => {
        this.addingAccount = false;
        this.addAccountSortOrder = null;
        this.message.success('Rând adăugat');
        this.loadAccountLines(this.selectedBudget.id);
      },
      (err) => {
        this.addingAccount = false;
        const msg = err.error?.error || 'Eroare la adăugarea contului';
        this.message.error(msg);
      }
    );
  }

  /** Rebuild all cached lookups from budgetLines + selectedBudget */
  rebuildCaches() {
    // Months in period
    this.cachedMonthsInPeriod = this._computeMonthsInPeriod();

    // Initialize expanded years: expand first and second year by default
    const years = [...new Set(this.cachedMonthsInPeriod.map(mp => mp.year))];
    this.expandedYears = new Set(years.slice(0, 2));

    // Line value lookup map: "sortOrder-month-year" -> line object
    this.lineValueMap.clear();
    for (const l of this.budgetLines) {
      this.lineValueMap.set(`${l.sortOrder}-${l.month}-${l.year}`, l);
    }

    // Unique rows
    const seen = new Set();
    const rows = [];
    for (const line of this.budgetLines) {
      if (!seen.has(line.sortOrder)) {
        seen.add(line.sortOrder);
        rows.push({
          sortOrder: line.sortOrder,
          description: line.description,
          isComputed: line.isComputed,
        });
      }
    }
    rows.sort((a, b) => a.sortOrder - b.sortOrder);
    this.cachedUniqueRows = rows;

    // Row totals
    this.rowTotalMap.clear();
    for (const line of this.budgetLines) {
      const cur = this.rowTotalMap.get(line.sortOrder) || 0;
      this.rowTotalMap.set(line.sortOrder, cur + (line.value || 0));
    }
  }

  private _computeMonthsInPeriod(): { month: number; year: number; label: string }[] {
    if (!this.selectedBudget) return [];

    // Detect annual mode: budget lines with month=0 indicate annual columns
    const isAnnual = this.budgetLines.some((l) => l.month === 0);

    if (isAnnual) {
      const result = [];
      for (let y = this.selectedBudget.periodStartYear; y <= this.selectedBudget.periodEndYear; y++) {
        result.push({ month: 0, year: y, label: String(y) });
      }
      return result;
    }

    const result = [];
    let m = this.selectedBudget.periodStartMonth;
    let y = this.selectedBudget.periodStartYear;
    const endM = this.selectedBudget.periodEndMonth;
    const endY = this.selectedBudget.periodEndYear;
    while (y < endY || (y === endY && m <= endM)) {
      result.push({
        month: m,
        year: y,
        label: this.months[m - 1].label + " " + y,
      });
      m++;
      if (m > 12) {
        m = 1;
        y++;
      }
    }
    return result;
  }

  getLineValue(sortOrder: number, month: number, year: number): any {
    return this.lineValueMap.get(`${sortOrder}-${month}-${year}`);
  }

  getRowTotal(sortOrder: number): number {
    return this.rowTotalMap.get(sortOrder) || 0;
  }

  onLineValueChange(sortOrder: number, month: number, year: number, newValue: number) {
    const line = this.lineValueMap.get(`${sortOrder}-${month}-${year}`);
    if (line) {
      line.value = newValue;
      // Recalculate row total
      let total = 0;
      for (const mp of this.cachedMonthsInPeriod) {
        const l = this.lineValueMap.get(`${sortOrder}-${mp.month}-${mp.year}`);
        if (l) total += (l.value || 0);
      }
      this.rowTotalMap.set(sortOrder, total);
    }
  }

  saveBudget() {
    if (!this.selectedBudget) return;
    this.savingBudget = true;

    const lines = this.budgetLines
      .filter((l) => !l.isComputed)
      .map((l) => ({ id: l.id, value: l.value }));

    const payload: any = { lines };
    if (this.dirtyAccountLines.length > 0) {
      payload.accountLines = this.dirtyAccountLines;
    }

    this.apiBackend
      .updateBudget(this.selectedBudget.id, payload)
      .subscribe(
        (res) => {
          this.selectedBudget = res.body;
          this.budgetLines = res.body.lines || [];
          this.rebuildCaches();
          this.dirtyAccountLines = [];
          this.savingBudget = false;
          this.message.success("Bugetul a fost salvat");
          // Reload account lines to get fresh IDs
          this.loadAccountLines(this.selectedBudget.id);
        },
        (err) => {
          this.savingBudget = false;
          this.message.error("Eroare la salvarea bugetului");
        }
      );
  }

  deleteBudget(budgetId: number) {
    this.apiBackend.deleteBudget(budgetId).subscribe(
      () => {
        this.message.success("Bugetul a fost șters");
        this.loadBudgets();
      },
      (err) => {
        this.message.error("Eroare la ștergerea bugetului");
      }
    );
  }

  // Email export
  showEmailModal = false;
  emailAddress = "";
  sendingEmail = false;

  openEmailModal() {
    this.emailAddress = this.currentUser?.email || "";
    this.showEmailModal = true;
  }

  sendByEmail() {
    if (!this.selectedBudget || !this.emailAddress) {
      this.message.warning("Introdu o adresă de email");
      return;
    }
    this.sendingEmail = true;
    this.apiBackend.sendBudgetByEmail(this.selectedBudget.id, this.emailAddress).subscribe(
      (res) => {
        this.sendingEmail = false;
        this.showEmailModal = false;
        this.message.success("Emailul a fost trimis cu succes");
      },
      (err) => {
        this.sendingEmail = false;
        this.message.error("Eroare la trimiterea emailului");
      }
    );
  }

  // Export
  exportExcel() {
    if (!this.selectedBudget) return;
    const url = `${environment.api_backend.url}/budgets/${this.selectedBudget.id}/export/excel`;
    this.httpClient.get(url, { responseType: "blob" }).subscribe(
      (response) => {
        saveAs(response, `buget-${this.selectedBudget.id}.xlsx`);
      },
      (err) => {
        this.message.error("Eroare la exportul Excel");
      }
    );
  }

  exportPdf() {
    if (!this.selectedBudget) return;
    const url = `${environment.api_backend.url}/budgets/${this.selectedBudget.id}/export/pdf`;
    this.httpClient.get(url, { responseType: "blob" }).subscribe(
      (response) => {
        const blob = new Blob([response], { type: "application/pdf" });
        saveAs(blob, `buget-${this.selectedBudget.id}.pdf`);
      },
      (err) => {
        this.message.error("Eroare la exportul PDF");
      }
    );
  }

  onAdminViewBudget(event: { budgetId: number; comparison: boolean }): void {
    this.adminViewBudgetId = event.budgetId;
    this.readOnly = true;
    if (event.comparison) {
      this.currentView = 'comparison';
      this.apiBackend.getBudget(event.budgetId).subscribe(
        (res) => {
          const budget = res.body;
          this.selectedComparisonGroup = {
            name: budget.name,
            realistic: budget.variantType === 'realistic' ? budget : null,
            pessimistic: budget.variantType === 'pessimistic' ? budget : null,
            period: '',
            dateCreated: budget.dateCreated,
            dateUpdated: budget.dateUpdated,
          };
          this.loadUserFiles();
        },
        () => this.message.error('Eroare la încărcarea bugetului')
      );
    } else {
      this.loadBudgetDetail(event.budgetId);
      this.currentView = 'editor';
    }
  }

  showMyBudget = false;

  switchToMyBudgetView(): void {
    this.showMyBudget = true;
    this.loadBudgets();
    this.loadBudgetGuidance();
  }

  backToAdminList(): void {
    this.showMyBudget = false;
    this.currentView = 'landing';
    this.readOnly = false;
    this.adminViewBudgetId = null;
    this.selectedBudget = null;
    this.selectedComparisonGroup = null;
    this.budgetLines = [];
    this.cachedUniqueRows = [];
    this.cachedMonthsInPeriod = [];
    this.lineValueMap.clear();
    this.rowTotalMap.clear();
    this.accountLinesBySort = {};
    this.expandSet.clear();
    this.dirtyAccountLines = [];
  }
}
